import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { COMPANHIAS, formatDataLocal } from '@agencia/shared';
import { CiaSelo, IconeAviao } from '@/components/CiaSelo';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { useApi } from '@/lib/useApi';
import { cancelado, nomeStatus, type ReservaDto, type ReservasCliente as Dados } from '@/lib/voos';

interface TrechoForm {
  numeroVoo: string;
  origem: string;
  destino: string;
  partida: string;
  chegada: string;
}

const VAZIO: TrechoForm = { numeroVoo: '', origem: '', destino: '', partida: '', chegada: '' };
const CIAS_FORM = ['G3', 'LA', 'AD'];

/** Voos do cliente: lidos dos bilhetes pela IA ou cadastrados à mão. */
export function ReservasCliente({ leadId }: { leadId: string }) {
  const { data, error, reload } = useApi<Dados>(`/crm/leads/${leadId}/reservas`);
  const [editando, setEditando] = useState<ReservaDto | 'nova' | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [lendo, setLendo] = useState<string | null>(null);

  async function ler(fileId: string) {
    setLendo(fileId);
    setMsg(null);
    try {
      const r = await api.post<{ reservas: number }>(`/crm/files/${fileId}/ler-voos`);
      setMsg({ kind: 'success', text: r.reservas ? `Bilhete lido: ${r.reservas} reserva(s).` : 'A IA não encontrou passagem aérea neste PDF.' });
      await reload();
    } catch (e) {
      setMsg({ kind: 'error', text: errorMessage(e) });
    } finally {
      setLendo(null);
    }
  }

  async function remover(r: ReservaDto) {
    if (!window.confirm(`Remover a reserva ${r.localizador} deste cliente?`)) return;
    try {
      await api.delete(`/crm/reservas/${r.id}`);
      await reload();
    } catch (e) {
      setMsg({ kind: 'error', text: errorMessage(e) });
    }
  }

  const naoLidos = data?.pdfs.filter((p) => !p.voosLidoEm) ?? [];
  const comErro = data?.pdfs.filter((p) => p.voosErro) ?? [];

  return (
    <section className="card">
      <div className="row between">
        <div>
          <h2>Voos do cliente</h2>
          <p className="muted small">
            {data?.iaLigada ? 'A IA lê os bilhetes em PDF salvos aqui e cadastra os voos sozinha. ' : ''}Aparecem em <Link to="/proximos-voos">Próximos voos</Link>.
          </p>
        </div>
        {editando === null && (
          <Button variant="secondary" onClick={() => setEditando('nova')}>
            Cadastrar reserva
          </Button>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}

      {editando !== null && (
        <ReservaForm
          leadId={leadId}
          reserva={editando === 'nova' ? null : editando}
          onCancel={() => setEditando(null)}
          onSaved={() => {
            setEditando(null);
            setMsg({ kind: 'success', text: 'Reserva salva.' });
            void reload();
          }}
        />
      )}

      {!data ? (
        <p className="muted small">Carregando…</p>
      ) : data.reservas.length === 0 && editando === null ? (
        <p className="muted small">Nenhum voo ainda. {naoLidos.length > 0 && data.iaLigada ? 'A IA está lendo os bilhetes deste cliente.' : 'Cadastre a reserva ou anexe o bilhete em PDF.'}</p>
      ) : (
        <div className="reservas">
          {data.reservas.map((r) => (
            <article key={r.id} className="reserva">
              <header className="reserva__top">
                <CiaSelo companhia={r.companhia} nome={r.companhiaNome} />
                <div>
                  <strong className="voo__loc">{r.localizador}</strong>
                  <div className="muted small">
                    {r.companhiaNome}
                    {r.pedidoCia && ` · pedido ${r.pedidoCia}`}
                    {r.passageiros.length > 0 && ` · ${r.passageiros.map((p) => p.nome).join(', ')}`}
                  </div>
                  <div className="muted small">
                    {r.origem === 'PDF_IA' ? `Lida do bilhete${r.arquivo ? ` (${r.arquivo.nome})` : ''}` : 'Cadastrada à mão'}
                    {r.venda && (
                      <>
                        {' · '}
                        <Link to={`/vendas/${r.venda.id}`}>venda #{r.venda.numero ?? '—'}</Link>
                      </>
                    )}
                    {r.bagagem && ` · ${r.bagagem}`}
                  </div>
                </div>
                <div className="row tight reserva__acoes">
                  {r.arquivo && (
                    <a className="btn btn--sm btn--secondary" href={`/api/crm/files/${r.arquivo.id}`}>
                      Bilhete (PDF)
                    </a>
                  )}
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => setEditando(r)}>
                    Editar
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => void remover(r)}>
                    Remover
                  </button>
                </div>
              </header>
              <ul className="reserva__trechos">
                {r.trechos.map((t) => (
                  <li key={t.id} className={cancelado(t) ? 'is-cancel' : ''}>
                    <span className="reserva__data">{formatDataLocal(t.partida)}</span>
                    <span className="reserva__rota">
                      <strong>{t.origem}</strong> {t.partida.slice(11, 16)}
                      <span className="reserva__seta" aria-hidden="true">
                        <IconeAviao size={14} />
                      </span>
                      <strong>{t.destino}</strong> {t.chegada ? t.chegada.slice(11, 16) : ''}
                    </span>
                    <span className="muted small">
                      voo {t.companhia} {t.numeroVoo}
                    </span>
                    {t.checkinFeitoEm && <span className="pill pill--ok">check-in feito</span>}
                    {t.statusVoo && data.monitorLigado && <span className="muted small">{nomeStatus(t.statusVoo)}</span>}
                    {t.alteracao && <span className={`pill ${t.alteracaoVistaEm ? 'pill--gray' : 'pill--warn'}`}>{t.alteracao}</span>}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      )}

      {data && data.pdfs.length > 0 && data.iaLigada && (
        <details className="reservas__pdfs">
          <summary className="small">
            Bilhetes em PDF deste cliente ({data.pdfs.length}){naoLidos.length ? ` · ${naoLidos.length} na fila da IA` : ''}
            {comErro.length ? ` · ${comErro.length} com erro` : ''}
          </summary>
          <ul className="plain-list small">
            {data.pdfs.map((p) => (
              <li key={p.id}>
                {p.nome} · <span className="muted">{p.voosErro ? `erro: ${p.voosErro}` : p.voosLidoEm ? 'lido' : 'na fila'}</span>{' '}
                <button type="button" className="linklike" disabled={lendo !== null} onClick={() => void ler(p.id)}>
                  {lendo === p.id ? 'lendo…' : p.voosLidoEm ? 'ler de novo' : 'ler agora'}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function ReservaForm({ leadId, reserva, onCancel, onSaved }: { leadId: string; reserva: ReservaDto | null; onCancel: () => void; onSaved: () => void }) {
  const ciaInicial = reserva?.companhia ?? 'G3';
  const [loc, setLoc] = useState(reserva?.localizador ?? '');
  const [cia, setCia] = useState(CIAS_FORM.includes(ciaInicial) ? ciaInicial : 'outra');
  const [ciaOutra, setCiaOutra] = useState(CIAS_FORM.includes(ciaInicial) ? '' : ciaInicial);
  const [pax, setPax] = useState(reserva?.passageiros.map((p) => p.nome).join('\n') ?? '');
  const [bagagem, setBagagem] = useState(reserva?.bagagem ?? '');
  const [sobrenome, setSobrenome] = useState(reserva?.sobrenome ?? '');
  const [pedido, setPedido] = useState(reserva?.pedidoCia ?? '');
  const [trechos, setTrechos] = useState<TrechoForm[]>(
    reserva?.trechos.map((t) => ({ numeroVoo: t.numeroVoo, origem: t.origem, destino: t.destino, partida: t.partida, chegada: t.chegada ?? '' })) ?? [{ ...VAZIO }],
  );
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mudar = (i: number, campo: keyof TrechoForm, valor: string) => setTrechos((l) => l.map((t, j) => (j === i ? { ...t, [campo]: valor } : t)));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    const companhia = cia === 'outra' ? ciaOutra.trim().toUpperCase() : cia;
    const body = {
      localizador: loc.trim().toUpperCase(),
      companhia,
      passageiros: pax.split('\n').map((s) => s.trim()).filter(Boolean),
      bagagem: bagagem.trim() || null,
      sobrenome: sobrenome.trim() || null,
      pedido: companhia === 'LA' ? pedido.trim() || null : null,
      trechos: trechos.map((t) => ({ ...t, origem: t.origem.trim().toUpperCase(), destino: t.destino.trim().toUpperCase(), chegada: t.chegada || null })),
    };
    setBusy(true);
    try {
      if (reserva) await api.put(`/crm/leads/${leadId}/reservas/${reserva.id}`, body);
      else await api.post(`/crm/leads/${leadId}/reservas`, body);
      onSaved();
    } catch (err) {
      setErro(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="reserva-form" onSubmit={(e) => void salvar(e)}>
      <h3>{reserva ? `Editar reserva ${reserva.localizador}` : 'Nova reserva'}</h3>
      {erro && <Alert>{erro}</Alert>}
      <div className="reserva-form__linha">
        <div className="field">
          <label htmlFor="rf-loc">Localizador</label>
          <input id="rf-loc" required maxLength={8} placeholder="Ex.: KLUSVJ" value={loc} onChange={(e) => setLoc(e.target.value.toUpperCase())} />
        </div>
        <div className="field">
          <label htmlFor="rf-cia">Companhia</label>
          <select id="rf-cia" value={cia} onChange={(e) => setCia(e.target.value)}>
            {CIAS_FORM.map((c) => (
              <option key={c} value={c}>
                {COMPANHIAS[c]} ({c})
              </option>
            ))}
            <option value="outra">Outra…</option>
          </select>
        </div>
        {cia === 'outra' && (
          <div className="field">
            <label htmlFor="rf-cia2">Código da companhia (2 letras)</label>
            <input id="rf-cia2" required maxLength={2} placeholder="Ex.: TP" value={ciaOutra} onChange={(e) => setCiaOutra(e.target.value.toUpperCase())} />
          </div>
        )}
      </div>
      <div className="reserva-form__linha">
        <div className="field">
          <label htmlFor="rf-pax">Passageiros (um por linha)</label>
          <textarea id="rf-pax" rows={3} value={pax} onChange={(e) => setPax(e.target.value)} placeholder={'MARIA SOUZA\nJOÃO SOUZA'} />
        </div>
        <div className="field">
          <label htmlFor="rf-sob">Sobrenome (como no bilhete)</label>
          <input id="rf-sob" maxLength={80} placeholder="Ex.: DA SILVA" value={sobrenome} onChange={(e) => setSobrenome(e.target.value.toUpperCase())} />
        </div>
        {cia === 'LA' && (
          <div className="field">
            <label htmlFor="rf-ped">Nº do pedido LATAM (abre direto na reserva)</label>
            <input id="rf-ped" maxLength={24} placeholder="Ex.: LA9571217XGID" value={pedido} onChange={(e) => setPedido(e.target.value.toUpperCase())} />
          </div>
        )}
        <div className="field">
          <label htmlFor="rf-bag">Bagagem (opcional)</label>
          <input id="rf-bag" maxLength={200} placeholder="Ex.: 1 mala de 23 kg + mão" value={bagagem} onChange={(e) => setBagagem(e.target.value)} />
        </div>
      </div>
      <h4>Voos</h4>
      {trechos.map((t, i) => (
        <fieldset key={i} className="reserva-form__trecho">
          <legend className="sr-only">Voo {i + 1}</legend>
          <div className="field">
            <label htmlFor={`rf-n-${i}`}>Nº do voo</label>
            <input id={`rf-n-${i}`} required maxLength={8} inputMode="numeric" placeholder="1633" value={t.numeroVoo} onChange={(e) => mudar(i, 'numeroVoo', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`rf-o-${i}`}>De</label>
            <input id={`rf-o-${i}`} required maxLength={3} placeholder="SSA" value={t.origem} onChange={(e) => mudar(i, 'origem', e.target.value.toUpperCase())} />
          </div>
          <div className="field">
            <label htmlFor={`rf-d-${i}`}>Para</label>
            <input id={`rf-d-${i}`} required maxLength={3} placeholder="GRU" value={t.destino} onChange={(e) => mudar(i, 'destino', e.target.value.toUpperCase())} />
          </div>
          <div className="field">
            <label htmlFor={`rf-p-${i}`}>Saída (hora local)</label>
            <input id={`rf-p-${i}`} required type="datetime-local" value={t.partida} onChange={(e) => mudar(i, 'partida', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`rf-c-${i}`}>Chegada</label>
            <input id={`rf-c-${i}`} type="datetime-local" value={t.chegada} onChange={(e) => mudar(i, 'chegada', e.target.value)} />
          </div>
          {trechos.length > 1 && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setTrechos((l) => l.filter((_, j) => j !== i))}>
              Tirar
            </button>
          )}
        </fieldset>
      ))}
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setTrechos((l) => [...l, { ...VAZIO }])}>
        + Adicionar voo (volta ou conexão)
      </button>
      <div className="row">
        <Button type="submit" loading={busy}>
          Salvar reserva
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
